<template>
    <div class="live-products-page">
        <Page>
            <template #title>
                <h1>{{ pageTitle }}</h1>
            </template>
            <template #default>
                <div v-if="loading" class="loading">
                    <Spinner />
                </div>
                <div v-else-if="error" class="error">
                    <Message severity="error">{{ error }}</Message>
                </div>
                <div v-else class="products-grid">
                    <Card
                        v-for="product in products"
                        :key="product.identity"
                        class="product-card"
                        @click="navigateToProduct(product.identity)"
                    >
                        <template #title>{{
                            product.name || product.identity
                        }}</template>
                        <template #content>
                            <div v-if="product.metrics" class="metrics-preview">
                                <div class="metric">
                                    <span class="metric-label"
                                        >Total Events:</span
                                    >
                                    <span class="metric-value">{{
                                        product.metrics.total_events
                                    }}</span>
                                </div>
                                <div class="metric">
                                    <span class="metric-label"
                                        >Error Rate:</span
                                    >
                                    <span class="metric-value"
                                        >{{
                                            product.metrics.error_rate.toFixed(
                                                2
                                            )
                                        }}%</span
                                    >
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>
            </template>
        </Page>
    </div>
</template>

<script setup lang="ts">
import Card from "primevue/card";
import Message from "primevue/message";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import Page from "@/components/Page.vue";
import { useStekManager } from "@/composables/stek-manager";
import { useClient, useServices } from "@/lib/openapi";
import type { LiveMetrics } from "@/lib/openapi/gen/types.gen";
import Spinner from "@/shared/Spinner.vue";

defineOptions({
    name: "ProductsPage",
});

const route = useRoute();
const router = useRouter();
const { stek } = useStekManager();
const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});
const client = useClient({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

const products = ref<
    Array<{ identity: string; name?: string; metrics?: LiveMetrics }>
>([]);
const loading = ref(false);
const error = ref<string | null>(null);

const stekName = computed(() => {
    return stek.value?.name || route.params.stekName;
});

const domainId = computed(() => {
    return route.query.domainId as string | undefined;
});

const domainName = ref<string | null>(null);

const pageTitle = computed(() => {
    if (domainName.value) {
        return `${stekName.value} - Products in ${domainName.value}`;
    }
    return `${stekName.value} - All Products`;
});

async function fetchProducts() {
    loading.value = true;
    error.value = null;
    try {
        const allProducts: Array<Record<string, unknown>> = [];

        if (domainId.value) {
            // Fetch products for a specific domain
            const productsResult =
                await api.startProductProductMgtGetProductsByDomainStartPost({
                    body: { domain_id: domainId.value },
                });
            if (productsResult.response.ok && productsResult.data) {
                let productsData: Array<Record<string, unknown>> = [];
                if (Array.isArray(productsResult.data)) {
                    productsData = productsResult.data;
                } else if (
                    productsResult.data &&
                    Array.isArray(productsResult.data.items)
                ) {
                    productsData = productsResult.data.items;
                }
                allProducts.push(...productsData);
            }

            // Fetch domain name for title
            const domainsResult =
                await api.startDomainDomainMgtGetDomainsByStekStartPost();
            if (domainsResult.response.ok && domainsResult.data) {
                let domainsData: Array<Record<string, unknown>> = [];
                if (Array.isArray(domainsResult.data)) {
                    domainsData = domainsResult.data;
                } else if (
                    domainsResult.data &&
                    Array.isArray(domainsResult.data.items)
                ) {
                    domainsData = domainsResult.data.items;
                }
                const domain = domainsData.find(
                    (d: Record<string, unknown>) =>
                        (d.identity || d.id || String(d)) === domainId.value
                );
                if (domain) {
                    domainName.value = domain.name || domain.title || null;
                }
            }
        } else {
            // Fetch all products across all domains
            const domainsResult =
                await api.startDomainDomainMgtGetDomainsByStekStartPost();
            if (domainsResult.response.ok && domainsResult.data) {
                let domainsData: Array<Record<string, unknown>> = [];
                if (Array.isArray(domainsResult.data)) {
                    domainsData = domainsResult.data;
                } else if (
                    domainsResult.data &&
                    Array.isArray(domainsResult.data.items)
                ) {
                    domainsData = domainsResult.data.items;
                }

                for (const domain of domainsData) {
                    const productsResult =
                        await api.startProductProductMgtGetProductsByDomainStartPost(
                            {
                                body: {
                                    domain_id:
                                        domain.identity ||
                                        domain.id ||
                                        String(domain),
                                },
                            }
                        );
                    if (productsResult.response.ok && productsResult.data) {
                        let productsData: Array<Record<string, unknown>> = [];
                        if (Array.isArray(productsResult.data)) {
                            productsData = productsResult.data;
                        } else if (
                            productsResult.data &&
                            Array.isArray(productsResult.data.items)
                        ) {
                            productsData = productsResult.data.items;
                        }
                        allProducts.push(...productsData);
                    }
                }
            }
        }

        // Fetch metrics for each product
        const productsWithMetrics = await Promise.all(
            allProducts.map(async (p: Record<string, unknown>) => {
                try {
                    const response = await client.post<LiveMetrics>({
                        url: "/stek/live_mgt/get_metrics/start",
                        body: {
                            product_id: p.identity || p.id || String(p),
                            hours: 24,
                        },
                    });

                    if (!response.response.ok || !response.data) {
                        return {
                            identity: p.identity || p.id || String(p),
                            name: p.name || p.title,
                        };
                    }

                    const metrics = response.data;
                    return {
                        identity: p.identity || p.id || String(p),
                        name: p.name || p.title,
                        metrics,
                    };
                } catch {
                    return {
                        identity: p.identity || p.id || String(p),
                        name: p.name || p.title,
                    };
                }
            })
        );
        products.value = productsWithMetrics;
    } catch (e) {
        error.value =
            e instanceof Error ? e.message : "Failed to load products";
    } finally {
        loading.value = false;
    }
}

function navigateToProduct(productId: string) {
    const orgName = route.params.orgName;
    const stekName = route.params.stekName;
    router.push({
        name: "liveProductPage",
        params: { orgName, stekName, productId },
    });
}

watch(
    [domainId],
    () => {
        fetchProducts();
    },
    { immediate: true }
);

onMounted(() => {
    fetchProducts();
});
</script>

<style scoped lang="scss">
.live-products-page {
    padding: 1rem;
}

.products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
}

.product-card {
    cursor: pointer;
    transition:
        transform 0.2s,
        box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
}

.metrics-preview {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.metric {
    display: flex;
    justify-content: space-between;
}

.metric-label {
    font-weight: 500;
}

.metric-value {
    font-weight: bold;
}

.loading,
.error {
    text-align: center;
    padding: 2rem;
}
</style>
